
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, ArrowUp, ArrowDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface WalletChargeProps {
  adminId: string;
}

interface WalletTransaction {
  id: string;
  amount: number;
  type: 'credit' | 'debit';
  description: string;
  timestamp: string | Date;
  reference?: string;
}

const WalletRecentCharges: React.FC<WalletChargeProps> = ({ adminId }) => {
  const { toast } = useToast();
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  useEffect(() => {
    if (adminId) {
      fetchRecentCharges();
    }
  }, [adminId]);

  const fetchRecentCharges = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: { 
          action: 'getWalletTransactions',
          adminId,
          limit: 10
        }
      });

      if (error) throw new Error(error.message);
      
      if (data?.transactions) {
        setTransactions(data.transactions);
      }
    } catch (error) {
      console.error('Error fetching recent charges:', error);
      toast({
        title: "Error",
        description: "Failed to load wallet transactions",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // While we wait for real data, let's generate some mock transactions
  useEffect(() => {
    if (transactions.length === 0 && !isLoading) {
      const mockTransactions: WalletTransaction[] = [
        {
          id: 't_1',
          amount: 500,
          type: 'credit',
          description: 'Wallet funding',
          timestamp: new Date(Date.now() - 3600000), // 1 hour ago
          reference: 'fund_123456'
        },
        {
          id: 't_2',
          amount: 75,
          type: 'debit',
          description: 'Amazon gift card purchase',
          timestamp: new Date(Date.now() - 86400000), // 1 day ago
          reference: 'order_789012'
        },
        {
          id: 't_3',
          amount: 50,
          type: 'debit',
          description: 'Starbucks gift card purchase',
          timestamp: new Date(Date.now() - 172800000), // 2 days ago
          reference: 'order_345678'
        },
        {
          id: 't_4',
          amount: 1000,
          type: 'credit',
          description: 'Monthly allocation',
          timestamp: new Date(Date.now() - 604800000), // 7 days ago
          reference: 'alloc_567890'
        },
        {
          id: 't_5',
          amount: 250,
          type: 'debit',
          description: 'Visa gift card purchase',
          timestamp: new Date(Date.now() - 1209600000), // 14 days ago
          reference: 'order_901234'
        },
        {
          id: 't_6',
          amount: 100,
          type: 'credit',
          description: 'Refund - canceled order',
          timestamp: new Date(Date.now() - 1296000000), // 15 days ago
          reference: 'refund_234567'
        },
        {
          id: 't_7',
          amount: 125,
          type: 'debit',
          description: 'Best Buy gift card purchase',
          timestamp: new Date(Date.now() - 1728000000), // 20 days ago
          reference: 'order_678901'
        },
        {
          id: 't_8',
          amount: 200,
          type: 'debit',
          description: 'Apple gift card purchase',
          timestamp: new Date(Date.now() - 2592000000), // 30 days ago
          reference: 'order_112233'
        },
        {
          id: 't_9',
          amount: 750,
          type: 'credit',
          description: 'Admin funding',
          timestamp: new Date(Date.now() - 2678400000), // 31 days ago
          reference: 'fund_445566'
        },
        {
          id: 't_10',
          amount: 300,
          type: 'debit',
          description: 'Google Play gift card purchase',
          timestamp: new Date(Date.now() - 3456000000), // 40 days ago
          reference: 'order_778899'
        }
      ];
      
      setTransactions(mockTransactions);
    }
  }, [isLoading, transactions]);

  return (
    <Card className="w-full h-full">
      <CardHeader className="pb-3">
        <CardTitle>Recent Wallet Activity</CardTitle>
        <CardDescription>
          Last 10 transactions on your reward wallet
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="border-t">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell className="text-sm">
                        {new Date(transaction.timestamp).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-sm">
                        {transaction.description}
                      </TableCell>
                      <TableCell>
                        <div className={`flex items-center ${transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {transaction.type === 'credit' ? (
                            <ArrowDown className="mr-1 h-4 w-4" />
                          ) : (
                            <ArrowUp className="mr-1 h-4 w-4" />
                          )}
                          ${transaction.amount.toFixed(2)}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {transaction.reference || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default WalletRecentCharges;
